/**
 * The mobile app's screens, as push deep-link destinations.
 *
 * A mirror of the Expo Router route table under `apps/mobile/app`, held here because the
 * console has no dependency on the mobile app and cannot import it. If a screen is added
 * or a path renamed there, this list must follow.
 *
 * It exists because a free-text field was the wrong control. The console used to advertise
 * `genexpay://activity` in a placeholder — a format that **both** the contract's
 * `broadcastPushRequestSchema` and the app itself reject, so every operator who followed
 * the placeholder wrote a broadcast whose link was refused. The contract is deliberately
 * narrow (an in-app path starting with `/`, or an `https://` URL, never a custom scheme)
 * because the device opens a non-path value with `Linking.openURL`, which will launch any
 * scheme the OS knows — `intent://` reaches other apps' components and `tel:` starts a
 * call. A picker makes the valid set the only set an operator can choose from.
 *
 * The API cannot catch a typo here: it only checks the leading `/`, so a stale path is a
 * notification that opens nothing rather than a validation error.
 */
export interface AppLinkOption {
  readonly value: string;
  readonly label: string;
}

/** The sentinel for "I want to type an https:// URL", never a real path (those start `/`). */
export const CUSTOM_APP_LINK = 'custom';

export const APP_LINK_OPTIONS: readonly AppLinkOption[] = [
  { value: '/', label: 'Home' },
  { value: '/activity', label: 'Activity' },
  { value: '/(tabs)/bills', label: 'Bills' },
  { value: '/(tabs)/account', label: 'Account' },
  { value: '/sell', label: 'Sell to naira' },
  { value: '/swap', label: 'Swap coins' },
  { value: '/convert', label: 'Convert' },
  // `/send` is the single money-out entry now — it opens a destination chooser rather than
  // going straight to a phone number. `?to=` skips that step for a banner that already knows
  // where it is sending someone.
  { value: '/send', label: 'Send money' },
  { value: '/send?to=user', label: 'Send to a GenEx user' },
  { value: '/send/bank', label: 'Send to a bank account' },
  { value: '/send/crypto', label: 'Send crypto out' },
  { value: '/deposit', label: 'Deposit' },
  { value: '/deposit/naira', label: 'Deposit by bank transfer' },
  { value: '/deposit/crypto', label: 'Deposit crypto' },
  { value: '/deposit/card', label: 'Deposit by card' },
  // Kept, and kept working: these are already out in the world on banners and pushes, and
  // both now redirect into `/send/*`. New banners should prefer the `/send` entries above.
  { value: '/withdraw/bank', label: 'Withdraw to bank (redirects to Send)' },
  { value: '/withdraw/crypto', label: 'Withdraw crypto (redirects to Send)' },
  { value: '/withdraw/addresses', label: 'Saved crypto addresses' },
  { value: '/giftcards', label: 'Gift cards' },
  { value: '/giftcards/redemptions', label: 'Gift card redemptions' },
  { value: '/support', label: 'Support' },
  { value: '/notifications', label: 'Notification settings' },
  { value: '/security', label: 'Security' },
  { value: '/profile', label: 'Profile' },
  { value: '/legal/terms', label: 'Terms' },
  { value: '/legal/privacy', label: 'Privacy policy' },
];
